// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import sharedActionCreators from "@modules/shared/actionCreator";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  CollapsibleWizard,
  Step,
} from "@components";
import type { HierarchyAssignment } from "@modules/service/types";
import type { Alert } from "@modules/service/reducer/alerts/types";

import StepAlertsCriteria from "./step-alerts-criteria";
import StepContent from "./step-content";
import StepAbsenceReasons from "./step-absence-reasons";
import StepRecipients from "./step-recipients";
import StepRestrictStaffGroups from "./step-restrict-staff-groups";
import StepServiceLength from "./step-service-length";

type OwnProps = $Exact<{
  showModal: boolean,
  defaultStep: 1 | 2 | 3 | 4 | 5 | 6,
  clientId: number,
  onCancel: () => void,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Alert,
  assignments: Array<HierarchyAssignment>,
}>;

type PropsFromDispatch = $Exact<{
  update: typeof actionCreators.UpdateAlert,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class AlertsEditModal extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    this.validate = new Ajv({ multipleOfPrecision: 2 }).compile(this.props.schema);
  }

  get isValid() {
    if (!this.validate || !this.props.draft) {
      return false;
    }

    const isValid = this.validate(removeNullAndUndefinedProps(this.props.draft?.content));
    if (!isValid) {
      console.warn("isValid errors", this.validate.errors);
    }

    return isValid;
  }

  handleSubmit = () => {
    if (this.props.draft) {
      this.props.update({
        clientId: this.props.clientId,
        alertId: this.props.draft?.id,
        draft: this.props.draft?.content,
      });
      this.props.onCancel();
    }
  };

  get isAbsenceReasonsValid() {
    const alertType = this.props.draft?.content?.details?.alertType;
    if (alertType !== "ABSENCE_REASON_ALERT_TYPE") {
      return true;
    }

    const reasons = this.props.draft?.content?.details?.absenceReasonAlertType?.absenceReasons;
    return Array.isArray(reasons) && reasons.length;
  }

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.props.onCancel}>
          {this.props.t("ALERT_EDIT_TITLE", { alertId: (this.props.draft || {}).id })}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          <CollapsibleWizard
            defaultStep={this.props.defaultStep}
            data={[
              { data: {}, valid: true },
              { data: {}, valid: true },
              { data: {}, valid: true },
              { data: {}, valid: true },
              { data: {}, valid: true },
              { data: {}, valid: true },
            ]}
          >
            <Step component={StepAlertsCriteria} title={this.props.t("ALERT_STEP1")} editing />
            <Step component={StepContent} title={this.props.t("ALERT_STEP2")} editing />
            <Step component={StepAbsenceReasons} title={this.props.t("ALERT_STEP3")} editing />
            <Step
              component={StepRecipients}
              title={this.props.t("ALERT_STEP4")}
              clientId={this.props.clientId}
              editing
            />
            <Step component={StepRestrictStaffGroups} title={this.props.t("ALERT_STEP5")} editing />
            <Step component={StepServiceLength} title={this.props.t("ALERT_STEP6")} editing />
          </CollapsibleWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.onCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="replay_circle_filled"
            disabled={!this.isValid || !this.isAbsenceReasonsValid}
            onClick={this.handleSubmit}
          >
            {this.props.t("SUBMIT")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getAlertSchema(state),
    draft: serviceSelectors.getAlertDraft(state),
    assignments: serviceSelectors.getAlertAssignments(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  update: actionCreators.UpdateAlert,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AlertsEditModal);
